import { useSelector } from 'react-redux';
import { isSlaReadingsAllowed } from 'constants/permissions';
import { prepareDoctorName } from 'infrastructure/functions';

import {
  hasClinicalRole,
  hasRNRole,
  hasTrackableRole,
  isAdmin,
  isAgencyDoctor,
  isBiller,
  isPatient,
  isPractice,
  isReadOnlyRole,
  isRoleDefault,
  isRoleNameAdministrator,
  isSmsDashboardOrUnregisteredRole,
  isTurkUserRole,
} from './userTypeHelper';
import {
  hasBillingToolsAccess,
  isAtRiskRole,
  isCriticalOrAtRiskAlertingRole,
  isCriticalRole,
  hasPatientAcceptanceRole,
  isMedicalAssistantRole,
  isSupervisorRole,
  hasAdminPanelAccess,
  isPriorityPatientAccessRole,
} from './accessManager/helpers';
import { isDesktop, isMobile, isTablet } from './deviceHelper';

import type { Roles } from 'infrastructure/enums';
import type { RootState } from 'store';

const useUser = () => {
  const user = useSelector((state: RootState) => state.user);
  const { getProfile: profileLoading } = useSelector(
    (state: RootState) => state.loading.effects.user,
  );

  const hasAnyRole = (rolesToCheck: Roles[]): boolean => {
    return rolesToCheck.some((role) => user.roleNames.includes(role));
  };

  return {
    isLoading: profileLoading,
    isAgencyDoctor: isAgencyDoctor(user.userType),
    isPractice: isPractice(user.userType),
    isPatient: isPatient(user.userType),
    isAdmin: isAdmin(user.userType),
    isRootAdmin: isAdmin(user.userType) && isRoleDefault(user.roleNames),
    isBiller: isBiller(user.roleNames) as boolean,
    isCriticalOrAtRiskAlertingRole: isCriticalOrAtRiskAlertingRole(
      user.roleNames,
    ) as boolean,
    isCriticalRole: isCriticalRole(user.roleNames) as boolean,
    isMedicalAssistantRole: isMedicalAssistantRole(user.roleNames) as boolean,
    isTurkUserRole: isTurkUserRole(user.roleNames),
    isAtRiskRole: isAtRiskRole(user.roleNames) as boolean,
    isSupervisorRole: isSupervisorRole(user.roleNames),
    isPriorityPatientAccessRole: isPriorityPatientAccessRole(user.roleNames),
    isReadOnlyRole: isReadOnlyRole(user.roleNames) as boolean,
    iamPatientAcceptance: hasPatientAcceptanceRole(user.roleNames),
    isSlaReadingsAllowed: isSlaReadingsAllowed(user.userType),
    isMobileDevice: isMobile(user.deviceType),
    isTabletDevice: isTablet(user.deviceType),
    isDesktopDevice: isDesktop(user.deviceType),
    billingToolsAccess: hasBillingToolsAccess(user.roleNames),
    hasAdminPanelAccess: hasAdminPanelAccess(user.roleNames),
    hasPatientAcceptanceRole: hasPatientAcceptanceRole(user.roleNames),
    hasClinicalRole: hasClinicalRole(user.roleNames),
    hasTrackableRole: (withMa = false) =>
      hasTrackableRole(user.roleNames, withMa),
    hasSmsRole:
      isAdmin(user.userType) ||
      isSmsDashboardOrUnregisteredRole(user.roleNames),
    isRoleNameAdministrator: isRoleNameAdministrator(user.roleNames),
    hasRNRole: hasRNRole(user.roleNames),
    hasAnyRole,
    fullName: prepareDoctorName({
      title: user.title,
      firstName: user.firstName,
      lastName: user.lastName,
    }),
    ...user,
  };
};

export default useUser;
